import { useAxiosCreate } from '@/backend/utils';
import { downloadBlobResponse } from '@/backend/download';

const http = useAxiosCreate('api');

export function fetchTransManagerConstructorConfig() {
  return http.get('transmanager/constructor/config').then(({ data }) => data);
}

function buildParams({ filters = {}, ...params } = {}) {
  const filterKeys = {
    dateFrom: 'date_from',
    dateTo: 'date_to',
    branch: 'branches',
    manager: 'managers',
    customer: 'customers',
    carrier: 'carriers',
    status: 'statuses',
    direction: 'directions',
    cityOut: 'cities_out',
    cityIn: 'cities_in',
  };
  Object.entries(filters).forEach(([key, values]) => {
    const paramKey = filterKeys[key] || key;
    if (Array.isArray(values) && values.length) params[paramKey] = values.join(',');
    else if (values !== '' && values != null) params[paramKey] = values;
  });
  return params;
}

export function fetchTransManagerConstructorPivot({ x, y, metric, metrics, step, xTop, yTop, xValues, yValues, filters } = {}) {
  const params = buildParams({
    x,
    metric,
    metrics: Array.isArray(metrics) ? metrics.join(',') : metrics,
    step,
    x_top: xTop,
    y_top: yTop,
    x_values: xValues?.length ? xValues.join(',') : undefined,
    y_values: yValues?.length ? yValues.join(',') : undefined,
    filters,
  });
  if (y) params.y = y;
  return http.get('transmanager/constructor/pivot', { params }).then(({ data }) => data);
}

export function fetchTransManagerFilterOptions(filters = {}) {
  return http.get('transmanager/filter-options', { params: buildParams({ filters }) }).then(({ data }) => ({
    branch: data.branches || [],
    manager: data.managers || [],
    customer: data.customers || [],
    carrier: data.carriers || [],
    status: data.statuses || [],
    direction: data.directions || [],
    cityOut: data.cities_out || [],
    cityIn: data.cities_in || [],
  }));
}

export function fetchTransManagerSummary(filters = {}) {
  return http.get('transmanager/summary', { params: buildParams({ filters }) }).then(({ data }) => data);
}

export function fetchTransManagerReports(filters = {}) {
  return http.get('transmanager/reports', { params: buildParams({ filters }) }).then(({ data }) => data);
}

export function fetchTransManagerDirections(filters = {}) {
  return http.get('transmanager/reports/directions', { params: buildParams({ filters }) }).then(({ data }) => data);
}

export function fetchTransManagerCounterparties(filters = {}) {
  return http.get('transmanager/reports/counterparties', { params: buildParams({ filters }) }).then(({ data }) => data);
}

export function fetchTransManagerPoints(filters = {}) {
  return http.get('transmanager/reports/route-points', { params: buildParams({ filters }) }).then(({ data }) => data);
}

export function fetchTransManagerConstructorTrips({ x, y, step, xValues, yValues, xKnown, yKnown, timeBins, sortBy, asc, limit = 50, offset = 0, filters } = {}) {
  const params = buildParams({
    x,
    step,
    sort_by: sortBy,
    asc,
    limit,
    offset,
    x_values: xValues?.length ? xValues.join(',') : undefined,
    y_values: yValues?.length ? yValues.join(',') : undefined,
    x_known: xKnown?.length ? xKnown.join(',') : undefined,
    y_known: yKnown?.length ? yKnown.join(',') : undefined,
    time_bins: timeBins?.length ? timeBins.join(',') : undefined,
    filters,
  });
  if (y) params.y = y;
  return http.get('transmanager/constructor/trips', { params }).then(({ data }) => data);
}

export async function downloadTransManagerConstructorXlsx({
  view = 'matrix',
  x,
  y,
  metric,
  step,
  xTop,
  yTop,
  xValues,
  yValues,
  xKnown,
  yKnown,
  timeBins,
  sortBy,
  asc,
  limit,
  offset,
  filters,
} = {}) {
  const params = buildParams({
    view,
    x,
    metric,
    step,
    x_top: xTop,
    y_top: yTop,
    x_values: xValues?.length ? xValues.join(',') : undefined,
    y_values: yValues?.length ? yValues.join(',') : undefined,
    x_known: xKnown?.length ? xKnown.join(',') : undefined,
    y_known: yKnown?.length ? yKnown.join(',') : undefined,
    time_bins: timeBins?.length ? timeBins.join(',') : undefined,
    sort_by: sortBy,
    asc,
    limit,
    offset,
    filters,
  });
  if (y) params.y = y;
  const response = await http.get('transmanager/constructor/export', { params, responseType: 'blob' });
  await downloadBlobResponse(response, `transmanager_constructor_${view}.xlsx`);
}
